import React from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { renderChart } from '@/utils/chartRenderer'

export const cleanChartContainersFromElement = (element: HTMLElement): void => {
  const chartContainers = element.querySelectorAll('.chart-container')
  chartContainers.forEach(chartContainer => chartContainer.innerHTML = '')
}

export const cleanChartContainersFromHTML = (content: string): string => {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = content
  cleanChartContainersFromElement(tempDiv)
  return tempDiv.innerHTML
}

export const getContentChartIds = (container: HTMLElement): string[] => {
  return Array.from(container.querySelectorAll('.chart-container')).map((element) => element.getAttribute('data-chart-id') || '')
}

/** Shared structure for trash button DOM and HTML so editor and headless append stay in sync. */
const TRASH_BUTTON_CLASS =
  'absolute top-2 right-2 inline-flex items-center justify-center whitespace-nowrap rounded-md border border-input bg-background text-xs text-foreground hover:bg-accent hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-6 w-6 z-10'
const TRASH_BUTTON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>`

const bindTrashButton = (
  button: HTMLButtonElement,
  targetId: string,
  updateContentCallback: () => void
): void => {
  button.classList.add('wysiwyg-removable-btn')
  button.type = 'button'
  button.setAttribute('data-remove-target-id', targetId)
  button.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    const targetElement = document.getElementById(targetId)
    if (targetElement) {
      targetElement.remove()
      updateContentCallback()
    }
  })
}

export const createTrashButton = (
  targetId: string,
  updateContentCallback: () => void
): HTMLButtonElement => {
  const trashButton = document.createElement('button')
  trashButton.className = TRASH_BUTTON_CLASS
  trashButton.title = 'Remove'
  trashButton.innerHTML = TRASH_BUTTON_SVG
  bindTrashButton(trashButton, targetId, updateContentCallback)
  return trashButton
}

/** HTML string for the same trash button; rebindExistingTrashButtons binds when editor mounts. */
export const getTrashButtonHtml = (targetId: string): string =>
  `<button type="button" class="${TRASH_BUTTON_CLASS} wysiwyg-removable-btn" data-remove-target-id="${targetId}" title="Remove">${TRASH_BUTTON_SVG}</button>`

/** Solution block wrapper + delete button HTML; same structure as WysiwygEditor solution:insertHtml. */
export const getSolutionBlockWithDeleteButtonHtml = (innerHtml: string): string => {
  const blockId = `solution-block-${Math.random().toString(36).slice(2, 10)}`
  return `<div class="relative my-2" id="${blockId}">${getTrashButtonHtml(blockId)}${innerHtml}</div><div>&nbsp;</div>`
}

/** Chart container HTML; same structure as WysiwygEditor chart:added. */
export const getChartContainerHtml = (chart: ChallengeService.Chart): string =>
  `<div class="chart-container w-full relative my-2 p-2 border border-border rounded-md overflow-hidden" data-chart-id="${chart.id}"></div><div>&nbsp;</div>`

export const rebindExistingTrashButtons = (
  updateContentCallback: () => void
): void => {
  const buttons = document.querySelectorAll<HTMLButtonElement>('.wysiwyg-removable-btn[data-remove-target-id]')
  buttons.forEach((button) => {
    const targetId = button.getAttribute('data-remove-target-id')
    if (!targetId) return
    bindTrashButton(button, targetId, updateContentCallback)
  })
}

/** Scan chart containers inside the editor and add a trash button to any that are missing one. */
export const ensureChartTrashButtons = (
  editor: HTMLElement,
  updateContentCallback: () => void
): void => {
  const charts = editor.querySelectorAll<HTMLElement>('.chart-container[data-chart-id]')
  charts.forEach((container) => {
    // Already has a trash button — skip
    if (container.querySelector('.wysiwyg-removable-btn')) return

    const chartId = container.getAttribute('data-chart-id')
    if (!chartId) return

    // Ensure the container has an id so the trash button can target it
    if (!container.id) {
      container.id = `chart-${chartId}`
    }

    const trashButton = createTrashButton(container.id, updateContentCallback)
    container.appendChild(trashButton)
  })
}

export const saveSelection = (editorRef: React.RefObject<HTMLDivElement | null>): Range | null => {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0 || !editorRef.current) {
    return null
  }

  const range = selection.getRangeAt(0)
  // Only save if the selection is within the editor
  if (editorRef.current.contains(range.commonAncestorContainer)) {
    return range.cloneRange()
  }
  return null
}

/** Convert a Range inside `root` to a character offset (counts only text chars). */
export const rangeToCharOffset = (root: HTMLElement, range: Range): number | null => {
  try {
    const preRange = document.createRange()
    preRange.selectNodeContents(root)
    preRange.setEnd(range.startContainer, range.startOffset)
    return preRange.toString().length
  } catch {
    return null
  }
}

/** Recreate a collapsed Range at the given character offset inside `root`. */
export const charOffsetToRange = (root: HTMLElement, offset: number): Range | null => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let remaining = offset
  let node: Text | null
  while ((node = walker.nextNode() as Text | null)) {
    const len = node.textContent?.length ?? 0
    if (remaining <= len) {
      const range = document.createRange()
      range.setStart(node, remaining)
      range.collapse(true)
      return range
    }
    remaining -= len
  }
  // Offset is past end — place at end of last text node (avoid selecting entire root if no text nodes)
  const lastNode = walker.currentNode
  if (!lastNode || lastNode === root) {
    // No text nodes — place cursor at end of root
    const range = document.createRange()
    range.selectNodeContents(root)
    range.collapse(false)
    return range
  }
  const range = document.createRange()
  range.setStart(lastNode, lastNode.textContent?.length ?? 0)
  range.collapse(true)
  return range
}

export const insertNodeAtSelection = (node: Node, editorRef: React.RefObject<HTMLDivElement | null>, savedRange?: Range | null) => {
  const editor = editorRef.current
  if (!editor) {
    return
  }

  // Try to find a valid range: prefer saved range (works even when editor is invisible),
  // then fall back to the current browser selection.
  const selection = window.getSelection()
  let range: Range | null = null

  if (savedRange && editor.contains(savedRange.commonAncestorContainer)) {
    range = savedRange
  } else if (selection && selection.rangeCount > 0) {
    const selRange = selection.getRangeAt(0)
    if (editor.contains(selRange.commonAncestorContainer)) {
      range = selRange
    }
  }

  if (!range) {
    // Fallback to append if no valid range in the editor
    editor.appendChild(node)
    return
  }

  let container: Node = range.commonAncestorContainer

  // If selection is inside a text node, move up to its element parent
  if (container.nodeType === Node.TEXT_NODE && container.parentNode) {
    container = container.parentNode
  }
  // If selection is already at editor level, insert directly at range
  if (container === editor) {
    range.insertNode(node)
  } else {
    // Walk up until we reach a direct child of the editor (top‑level block)
    while (container.parentNode && container.parentNode !== editor) {
      container = container.parentNode
    }

    if (container === editor) {
      // Selection is directly in the editor; insert at the selection position
      range.insertNode(node)
    } else {
      // Insert as a sibling of the top‑level container (never nested inside)
      editor.insertBefore(node, container.nextSibling)
    }
  }

  // Move cursor after the inserted node
  if (selection) {
    try {
      const newRange = document.createRange()
      newRange.setStartAfter(node)
      newRange.collapse(true)
      selection.removeAllRanges()
      selection.addRange(newRange)
    } catch {
      // May fail if editor is invisible; safe to ignore
    }
  }
}

export const initializeChart = (
  id: string,
  chart: ChallengeService.Chart,
  editorRef: React.RefObject<HTMLDivElement | null>,
  readOnly: boolean,
  updateContent: () => void
): (() => void) | void  => {
  if (!chart.data) {
    return
  }
  const chartContainer = editorRef.current?.querySelector(`div.chart-container[data-chart-id="${id}"]`) as HTMLElement
  if (chartContainer) {
    if (!readOnly) {
      const chartContainerId = chartContainer.id || `chart-${id}`
      chartContainer.id = chartContainerId
      const trashButton = createTrashButton(chartContainerId, updateContent)
      chartContainer.appendChild(trashButton)
    }

    const chartDescriptionElement = document.createElement('div')
    chartDescriptionElement.textContent = `${chart.query}`
    chartDescriptionElement.className = 'text-xs text-muted-foreground italic truncate max-w-[calc(100%-2rem)] font-mono'
    chartDescriptionElement.contentEditable = 'false'
    chartContainer.prepend(chartDescriptionElement)

    const canvasContainer = document.createElement('div')
    canvasContainer.className = `w-full ${chart.chartType === 'table' ? '' : 'h-64'}`
    chartContainer.appendChild(canvasContainer)
    chartContainer.contentEditable = 'false'
    const cleanup = renderChart(chart, canvasContainer)
    return () => {
      cleanup?.()
    }

  }
}

